import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient, useMutation, useInfiniteQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import { useAuth } from "@/hooks/use-auth";
import { useDeckWords } from "@/hooks/use-deck-words";
import { apiDelete, apiGet, apiPatch, apiPost } from "@/lib/api-client";

/**
 * Deck filters → query string.
 *
 * The SQL function wants NULL for "no filter"; the API accepts `all`/empty and
 * normalises it server-side, so the SPA sends what the user selected and the
 * service decides what that means.
 */
function deckQuery(params: {
    search: string;
    filter: string;
    certLevel: string;
    wordType: string;
    calendarDate: string | null;
    tier: string | null;
}): URLSearchParams {
    const query = new URLSearchParams();
    if (params.search) query.set("search", params.search);
    if (params.filter) query.set("filter", params.filter);
    if (params.certLevel) query.set("certLevel", params.certLevel);
    if (params.wordType) query.set("wordType", params.wordType);
    if (params.calendarDate) query.set("calendarDate", params.calendarDate);
    if (params.tier) query.set("tier", params.tier);
    return query;
}
// Use the UI package's toast store — that's the one the mounted <Toaster> subscribes to.
// (The app-local @/hooks/use-toast is a separate, unrendered store.)
import { useToast } from "@lyder-talk/ui";
import { auth } from "@lyder-talk/core/lib/auth-client";

export interface SavedVocabulary {
    id: string;
    word: string;
    definition: string;
    example: string;
    ipa: string | null;
    type: string | null;
    cert_level: string | null;
    source_session_id: string | null;
    topic_title: string | null;
    source_session_slug: string | null;
    mastered_at: string | null;
    created_at: string;
    // Spaced repetition fields
    last_reviewed_at: string | null;
    next_review_at: string | null;
    review_count: number;
    last_difficulty: 'again' | 'hard' | 'good' | 'easy' | null;
    ease_factor: number;
    // FSRS fields
    stability: number;
    difficulty: number;
    fsrs_state: number;
    // Enrichment fields
    context_sentences: ContextSentence[] | null;
    word_relationships: WordRelationships | null;
}

export interface ContextSentence {
    text: string;
    difficulty: 'easy' | 'medium' | 'hard';
    translation_hint: string;
}

export interface WordRelationships {
    synonyms: string[];
    antonyms: string[];
    collocations: Array<{ phrase: string; example: string }>;
}

export type ReviewDifficulty = 'again' | 'hard' | 'good' | 'easy';

export interface ReviewResult {
    id: string;
    next_review_at: string;
    review_count: number;
    ease_factor: number;
    mastered_at: string | null;
}

export interface VocabularyStats {
    total_saved: number;
    total_well_reviewed: number;
    total_mastered: number;
    reviewed_today: number;
    due_for_review: number;
}

export interface VocabularyToSave {
    word: string;
    definition: string;
    example: string;
    ipa?: string;
    type?: string;
    certLevel?: string;
    sourceSessionId?: string;
    contextSentences?: ContextSentence[];
    wordRelationships?: WordRelationships;
}

export interface VocabularyUpdate {
    id: string;
    word?: string;
    ipa?: string | null;
    type?: string | null;
    cert_level?: string | null;
    definition?: string;
    example?: string;
    context_sentences?: ContextSentence[] | null;
    word_relationships?: WordRelationships | null;
}

/**
 * Hook to manage saved vocabulary for the current user.
 *
 * **The full deck is opt-in.** Most consumers only ask "is this word saved?" or
 * need the save/remove mutations, but the list query selects every column of
 * every row — roughly 874KB for a 1,000-word deck (measured). Mounted on
 * session, dictionary, practice and home surfaces, that was the single largest
 * payload the app pulled.
 *
 * `isWordSaved` is therefore backed by `useDeckWords`, a one-column query shared
 * with the example highlighter, so saved-state costs ~30KB for the same deck and
 * dedupes onto a request other components already make.
 *
 * Pass `includeList: true` only where the rows themselves are rendered or
 * reviewed. `includeList: false` (the default) keeps the mutations, stats and
 * saved-state working without the deck payload.
 */
export const useSavedVocabulary = (options: { includeList?: boolean } = {}) => {
    const { includeList = false } = options;
    const queryClient = useQueryClient();
    const { data: isAuthenticated, userId } = useAuth();
    const { toast } = useToast();

    // Saved-state comes from the one-column deck-words query, not the full deck.
    // Shared key with `useDeckWords`, so a page that already highlights example
    // sentences pays nothing extra for this.
    const deckWords = useDeckWords();
    const savedWordSet = useMemo(
        () => new Set(deckWords.map((word) => word.toLowerCase())),
        [deckWords]
    );

    // Fetch all saved vocabulary
    const vocabularyQuery = useQuery({
        queryKey: queryKeys.savedVocabulary.byUser(userId),
        // `userId` arrives a tick late (AuthContext starts at `null` and fills in
        // after `getSession()`), so a key-only gate fired once under
        // `[..., null]` and again under the real id — the same deck read twice.
        enabled: includeList && Boolean(userId),
        queryFn: async (): Promise<SavedVocabulary[]> => {
            // Typed endpoint, called directly. No PostgREST fallback: the API is
            // the only data path now, so a failure here is a real failure rather
            // than something to hide behind a second implementation.
            const result = await apiGet<{ count: number; rows: SavedVocabulary[] }>(
                "/v1/vocabulary?limit=1000"
            );
            return result.rows;
        },
        staleTime: 1000 * 30, // Cache for 30 seconds (was 2 minutes)
        refetchOnMount: true, // Always refetch when component mounts
        refetchOnWindowFocus: false,
    });

    // Fetch vocabulary stats
    const statsQuery = useQuery({
        queryKey: queryKeys.savedVocabularyStats.byUser(userId),
        // Without this the query ran under `[..., null]` while auth resolved and
        // then again under the real id: two `get_saved_vocabulary_stats` RPCs on
        // every page load for one number.
        enabled: Boolean(userId),
        queryFn: async (): Promise<VocabularyStats> => {
            return apiGet<VocabularyStats>("/v1/vocabulary/stats");
        },
        staleTime: 1000 * 60, // Cache for 1 minute
        refetchOnWindowFocus: false,
    });

    // Save vocabulary mutation
    const saveMutation = useMutation({
        mutationFn: async (vocab: VocabularyToSave) => {
            const {
                data: { session },
            } = await auth.getSession();

            if (!session?.user) {
                throw new Error("You must be logged in to save vocabulary");
            }

            // The endpoint owns the de-duplication rule: it upserts on the
            // unique (user_id, word) index and reports `already_saved`, so the
            // client no longer needs a check-then-insert round trip.
            return apiPost<{ action: "saved" | "already_saved"; id: string }>("/v1/vocabulary", {
                word: vocab.word,
                definition: vocab.definition,
                example: vocab.example,
                ipa: vocab.ipa || null,
                type: vocab.type || null,
                certLevel: vocab.certLevel || null,
                sourceSessionId: vocab.sourceSessionId || null,
                contextSentences: vocab.contextSentences || null,
                wordRelationships: vocab.wordRelationships || null,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.savedVocabulary.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.savedVocabularyStats.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.vocabularySaveStatus.all });
        },
    });

    // Remove vocabulary mutation
    const removeMutation = useMutation({
        mutationFn: async (word: string) => {
            await apiDelete(`/v1/vocabulary/${encodeURIComponent(word)}`);
            return { action: "removed" as const };
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.savedVocabulary.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.savedVocabularyStats.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.vocabularySaveStatus.all });
        },
    });



    // Update vocabulary fields mutation (for enriching missing info)
    const updateMutation = useMutation({
        mutationFn: async (update: VocabularyUpdate) => {
            const { id, ...fields } = update;
            // Only non-undefined fields; the service enforces the allowlist.
            const updateData: Record<string, string | null | ContextSentence[] | WordRelationships> = {};
            if (fields.word !== undefined) updateData.word = fields.word;
            if (fields.ipa !== undefined) updateData.ipa = fields.ipa;
            if (fields.type !== undefined) updateData.type = fields.type;
            if (fields.cert_level !== undefined) updateData.cert_level = fields.cert_level;
            if (fields.definition !== undefined) updateData.definition = fields.definition;
            if (fields.example !== undefined) updateData.example = fields.example;
            if (fields.context_sentences !== undefined) updateData.context_sentences = fields.context_sentences;
            if (fields.word_relationships !== undefined) updateData.word_relationships = fields.word_relationships;

            await apiPatch(`/v1/vocabulary/${encodeURIComponent(id)}`, updateData);
            return { id, ...updateData };
        },
        onSuccess: (result) => {
            // Optimistically update the local cache
            queryClient.setQueryData<SavedVocabulary[]>(queryKeys.savedVocabulary.all, (oldData) => {
                if (!oldData) return oldData;
                return oldData.map((vocab) =>
                    vocab.id === result.id
                        ? { ...vocab, ...result }
                        : vocab
                );
            });
            queryClient.invalidateQueries({ queryKey: queryKeys.savedVocabularyStats.all });
            // Refresh the paginated deck views (prefix-match all filter variants).
            queryClient.invalidateQueries({ queryKey: ["saved-vocabulary", "list"] });
            queryClient.invalidateQueries({ queryKey: ["saved-vocabulary", "facets"] });
        },
    });

    // Update vocabulary review mutation (spaced repetition)
    const reviewMutation = useMutation({
        mutationFn: async ({ id, difficulty }: { id: string; difficulty: ReviewDifficulty }): Promise<ReviewResult> => {
            const {
                data: { session },
            } = await auth.getSession();

            if (!session?.user) {
                throw new Error("You must be logged in to review vocabulary");
            }

            return await apiPost<ReviewResult>(`/v1/vocabulary/${encodeURIComponent(id)}/reviews`, { difficulty });
        },
        onSuccess: (result, { id }) => {
            // Detect a fresh graduation (mastered_at went from null -> set) for celebration
            const prev = queryClient
                .getQueryData<SavedVocabulary[]>(queryKeys.savedVocabulary.byUser(userId))
                ?.find((v) => v.id === id);
            const justMastered = !!result.mastered_at && !prev?.mastered_at;

            // Optimistically update the local cache
            queryClient.setQueryData<SavedVocabulary[]>(queryKeys.savedVocabulary.byUser(userId), (oldData) => {
                if (!oldData) return oldData;
                return oldData.map((vocab) =>
                    vocab.id === id
                        ? {
                            ...vocab,
                            last_reviewed_at: new Date().toISOString(),
                            next_review_at: result.next_review_at,
                            review_count: result.review_count,
                            ease_factor: result.ease_factor,
                            mastered_at: result.mastered_at,
                        }
                        : vocab
                );
            });
            queryClient.invalidateQueries({ queryKey: queryKeys.savedVocabularyStats.all });
            // Refresh the paginated deck views (a review can change tier/filter).
            queryClient.invalidateQueries({ queryKey: ["saved-vocabulary", "list"] });
            queryClient.invalidateQueries({ queryKey: ["saved-vocabulary", "facets"] });
            queryClient.invalidateQueries({ queryKey: ["saved-vocabulary", "calendar"] });
            // A review changes `next_review_at`, which is exactly what the
            // reminder's due-items query filters on.
            queryClient.invalidateQueries({ queryKey: ["saved-vocabulary", "due-items"] });

            if (justMastered) {
                toast({
                    title: "🎉 Word mastered!",
                    description: prev?.word
                        ? `"${prev.word}" graduated to your mastered list.`
                        : "A word graduated to your mastered list.",
                });
            }
        },
    });

    const saveVocabulary = useCallback(
        async (vocab: VocabularyToSave) => {
            try {
                return await saveMutation.mutateAsync(vocab);
            } catch (err) {
                console.error("Error saving vocabulary:", err);
                throw err;
            }
        },
        [saveMutation]
    );

    const removeVocabulary = useCallback(
        async (word: string) => {
            try {
                return await removeMutation.mutateAsync(word);
            } catch (err) {
                console.error("Error removing vocabulary:", err);
                throw err;
            }
        },
        [removeMutation]
    );



    const updateReview = useCallback(
        async (id: string, difficulty: ReviewDifficulty): Promise<ReviewResult> => {
            try {
                return await reviewMutation.mutateAsync({ id, difficulty });
            } catch (err) {
                console.error("Error updating review:", err);
                throw err;
            }
        },
        [reviewMutation]
    );

    const updateVocabulary = useCallback(
        async (update: VocabularyUpdate) => {
            try {
                return await updateMutation.mutateAsync(update);
            } catch (err) {
                console.error("Error updating vocabulary:", err);
                throw err;
            }
        },
        [updateMutation]
    );

    const isWordSaved = useCallback(
        (word: string): boolean => {
            if (!word) return false;
            return savedWordSet.has(word.trim().toLowerCase());
        },
        [savedWordSet]
    );

    return {
        vocabulary: vocabularyQuery.data || [],
        stats: statsQuery.data || { total_saved: 0, total_well_reviewed: 0, total_mastered: 0, reviewed_today: 0, due_for_review: 0 },
        loading: vocabularyQuery.isLoading,
        statsLoading: statsQuery.isLoading,
        isSaving: saveMutation.isPending,
        isRemoving: removeMutation.isPending,
        isUpdating: updateMutation.isPending,
        isUpdatingReview: reviewMutation.isPending,
        error: vocabularyQuery.error,
        saveVocabulary,
        removeVocabulary,
        updateVocabulary,
        updateReview,
        isWordSaved,
        refresh: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.savedVocabulary.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.savedVocabularyStats.all });
        },
    };
};

/**
 * Hook to check if a specific word is saved
 */
export const useVocabularySaveStatus = (word: string) => {
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: queryKeys.vocabularySaveStatus.byWord(word),
        queryFn: async (): Promise<boolean> => {
            const {
                data: { session },
            } = await auth.getSession();

            if (!session?.user || !word) {
                return false;
            }

            const { saved } = await apiGet<{ saved: boolean }>(
                `/v1/vocabulary/saved?word=${encodeURIComponent(word)}`
            );
            return saved;
        },
        enabled: !!word,
        staleTime: 1000 * 60, // Cache for 1 minute
        refetchOnWindowFocus: false,
    });

    return {
        isSaved: query.data || false,
        loading: query.isLoading,
        error: query.error,
        refresh: () => queryClient.invalidateQueries({ queryKey: queryKeys.vocabularySaveStatus.byWord(word) }),
    };
};

// ─────────────────────────────────────────────────────────────
// Server-side paginated deck (used by MyVocabulary). Filtering,
// sorting, facets and calendar are computed by RPCs so the page
// no longer pulls the entire deck to aggregate client-side.
// ─────────────────────────────────────────────────────────────

export interface SavedVocabularyListParams {
    search?: string;
    /** 'all' | 'due' | 'reviewed' | 'no review' | 'incomplete' */
    filter?: string;
    certLevel?: string;
    wordType?: string;
    /** YYYY-MM-DD, or null */
    calendarDate?: string | null;
    /** 'new' | 'learning' | 'reviewing' | 'mastered', or null */
    tier?: string | null;
    pageSize?: number;
    /**
     * Skip the query. The sidebar renders this as a collapsed list, so it passes
     * its open state rather than loading a deck page for every page load.
     */
    enabled?: boolean;
}

export interface SavedVocabularyFacets {
    cert_levels: string[];
    word_types: string[];
    incomplete_count: number;
    reviewed_count: number;
    no_review_count: number;
    tier_new: number;
    tier_learning: number;
    tier_reviewing: number;
    tier_mastered: number;
    /**
     * Per-field enrichment gaps, from migration 0151. These OVERLAP by design —
     * one word missing three fields is counted in three of them — so they are
     * independent tallies, never parts of a whole that sums to
     * `incomplete_count`. See the migration header for why.
     */
    missing_ipa: number;
    missing_type: number;
    missing_cert_level: number;
    missing_example: number;
    missing_context: number;
    missing_relationships: number;
}

export interface VocabularyCalendarEntry {
    id: string;
    word: string;
    last_reviewed_at: string | null;
    next_review_at: string | null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapRowToSavedVocabulary(r: any): SavedVocabulary {
    return {
        id: r.id,
        word: r.word,
        definition: r.definition,
        example: r.example,
        ipa: r.ipa,
        type: r.type,
        cert_level: r.cert_level,
        source_session_id: r.source_session_id,
        topic_title: r.topic_title ?? null,
        source_session_slug: r.source_session_slug ?? null,
        mastered_at: r.mastered_at,
        created_at: r.created_at,
        last_reviewed_at: r.last_reviewed_at,
        next_review_at: r.next_review_at,
        review_count: Number(r.review_count ?? 0),
        last_difficulty: r.last_difficulty,
        ease_factor: Number(r.ease_factor ?? 2.5),
        stability: Number(r.stability ?? 0),
        difficulty: Number(r.difficulty ?? 0),
        fsrs_state: Number(r.fsrs_state ?? 0),
        context_sentences: r.context_sentences ?? null,
        word_relationships: r.word_relationships ?? null,
    };
}

/**
 * Rows per page for the paginated deck. Sized so the first page fills a screen
 * in both grid and list view without a second round trip.
 */
export const DECK_PAGE_SIZE = 48;

/** The four columns the dictionary's Saved Words tab renders. */
export interface SavedVocabularyWord {
    id: string;
    word: string;
    definition: string;
    type: string | null;
}

/** Rows per page for the lean word list — small enough for a slide-over panel. */
export const SAVED_WORDS_PAGE_SIZE = 100;

/**
 * Infinite paginated deck. Each page is `get_saved_vocabulary(...)` with all
 * filters applied server-side; `total` comes from the window count.
 */
export const useSavedVocabularyList = (params: SavedVocabularyListParams = {}) => {
    const { userId } = useAuth();
    const {
        search = "",
        filter = "all",
        certLevel = "all",
        wordType = "all",
        calendarDate = null,
        tier = null,
        pageSize = DECK_PAGE_SIZE,
        enabled = true,
    } = params;

    return useInfiniteQuery({
        queryKey: queryKeys.savedVocabulary.list(userId, search, filter, certLevel, wordType, calendarDate, tier, pageSize),
        enabled: enabled && !!userId,
        initialPageParam: 0,
        queryFn: async ({ pageParam }) => {
            const query = deckQuery({ search, filter, certLevel, wordType, calendarDate, tier });
            query.set("limit", String(pageSize));
            query.set("offset", String(pageParam));

            const page = await apiGet<{ items: SavedVocabulary[]; total: number }>(
                `/v1/vocabulary/page?${query.toString()}`
            );

            return {
                items: page.items.map(mapRowToSavedVocabulary),
                total: page.total,
                offset: pageParam as number,
                pageSize,
            };
        },
        getNextPageParam: (lastPage) => {
            const next = lastPage.offset + lastPage.pageSize;
            return next < lastPage.total ? next : undefined;
        },
        staleTime: 1000 * 30,
        refetchOnWindowFocus: false,
        placeholderData: (prev) => prev,
    });
};

/**
 * Word + definition + type, paged — for lists that render nothing else.
 *
 * The dictionary's Saved Words tab used `useSavedVocabulary({ includeList: true })`,
 * which selects **every** column of every row (741 B/row measured) plus a
 * `practice_sessions` join, to render a word, its definition and a type badge
 * (~90 B/row). The rest is review state and enrichment that tab never shows, and
 * there was no `.limit()` — the whole deck arrived in one response.
 */
export const useSavedVocabularyWords = (
    options: { pageSize?: number; enabled?: boolean } = {}
) => {
    const { userId } = useAuth();
    const { pageSize = SAVED_WORDS_PAGE_SIZE, enabled = true } = options;

    return useInfiniteQuery({
        queryKey: queryKeys.savedVocabulary.wordList(userId, pageSize),
        enabled: enabled && Boolean(userId),
        initialPageParam: 0,
        queryFn: async ({ pageParam }) => {
            const from = pageParam as number;
            const query = new URLSearchParams({
                limit: String(pageSize),
                offset: String(from),
            });

            const page = await apiGet<{ items: SavedVocabularyWord[]; total: number }>(
                `/v1/vocabulary/page?${query.toString()}`
            );

            return { items: page.items, total: page.total, offset: from };
        },
        getNextPageParam: (lastPage) => {
            const next = lastPage.offset + lastPage.items.length;
            return lastPage.items.length > 0 && next < lastPage.total ? next : undefined;
        },
        staleTime: 1000 * 30,
        refetchOnWindowFocus: false,
    });
};

/** Deck facets: distinct cert levels/types + status counts + tier counts. */
export const useSavedVocabularyFacets = () => {
    const { userId } = useAuth();
    return useQuery({
        queryKey: queryKeys.savedVocabulary.facets(userId),
        enabled: !!userId,
        queryFn: async (): Promise<SavedVocabularyFacets> => {
            const r = (await apiGet<Record<string, unknown>>("/v1/vocabulary/facets")) ?? {};
            return {
                cert_levels: (r.cert_levels ?? []) as string[],
                word_types: (r.word_types ?? []) as string[],
                incomplete_count: Number(r.incomplete_count ?? 0),
                reviewed_count: Number(r.reviewed_count ?? 0),
                no_review_count: Number(r.no_review_count ?? 0),
                tier_new: Number(r.tier_new ?? 0),
                tier_learning: Number(r.tier_learning ?? 0),
                tier_reviewing: Number(r.tier_reviewing ?? 0),
                tier_mastered: Number(r.tier_mastered ?? 0),
                missing_ipa: Number(r.missing_ipa_count ?? 0),
                missing_type: Number(r.missing_type_count ?? 0),
                missing_cert_level: Number(r.missing_cert_level_count ?? 0),
                missing_example: Number(r.missing_example_count ?? 0),
                missing_context: Number(r.missing_context_count ?? 0),
                missing_relationships: Number(r.missing_relationships_count ?? 0),
            };
        },
        staleTime: 1000 * 30,
        refetchOnWindowFocus: false,
    });
};

/** Lightweight review-date rows for the calendar sidebar (no heavy columns). */
export const useVocabularyReviewCalendar = () => {
    const { userId } = useAuth();
    return useQuery({
        queryKey: queryKeys.savedVocabulary.calendar(userId),
        enabled: !!userId,
        queryFn: async (): Promise<VocabularyCalendarEntry[]> => {
            return (await apiGet<VocabularyCalendarEntry[]>("/v1/vocabulary/review-calendar")) ?? [];
        },
        staleTime: 1000 * 30,
        refetchOnWindowFocus: false,
    });
};

/**
 * Imperatively fetch full rows matching the given filters (up to `limit`) —
 * used by "Fill All" enrichment, which needs the actual word rows for every
 * incomplete word, not just the loaded page.
 */
export async function fetchSavedVocabularyRows(
    params: SavedVocabularyListParams = {},
    limit = 1000
): Promise<SavedVocabulary[]> {
    const { search = "", filter = "all", certLevel = "all", wordType = "all", calendarDate = null, tier = null } = params;
    const query = deckQuery({ search, filter, certLevel, wordType, calendarDate, tier });
    query.set("limit", String(limit));
    query.set("offset", "0");

    const page = await apiGet<{ items: SavedVocabulary[] }>(`/v1/vocabulary/page?${query.toString()}`);
    return page.items.map(mapRowToSavedVocabulary);
}

/**
 * Imperatively fetch ALL ids matching the current filters — for true
 * "select all" / practice-all / Fill-All across unloaded pages.
 */
export async function fetchSavedVocabularyIds(params: SavedVocabularyListParams = {}): Promise<string[]> {
    const { search = "", filter = "all", certLevel = "all", wordType = "all", calendarDate = null, tier = null } = params;
    const query = deckQuery({ search, filter, certLevel, wordType, calendarDate, tier });
    const { ids } = await apiGet<{ ids: string[] }>(`/v1/vocabulary/ids?${query.toString()}`);
    return ids;
}

